import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputBase,
  Link,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheckCircle,
  FiChevronRight,
  FiClock,
  FiGrid,
  FiSearch,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { useGetAgentWhatsappStatusQuery } from "../../redux/api/agentWhatsappApi";
import AgentWhatsappCard, {
  ConnectWhatsAppDialog,
  useAgentWhatsappConnect,
} from "./AgentWhatsappCard";
import AgentWhatsappInbox from "./AgentWhatsappInbox";
import { WA_CALLBACK_MESSAGES } from "./data";
import { CATEGORIES, INTEGRATIONS } from "./integrations";
import { useTokens } from "./tokens";

const StatusChip = ({ connected }) => {
  const tokens = useTokens();
  return (
    <Chip
      size="small"
      label={connected ? "Connected" : "Not connected"}
      sx={{
        fontWeight: 700,
        bgcolor: connected ? tokens.successBg : tokens.softFaint,
        color: connected ? tokens.success : tokens.inkMuted,
      }}
    />
  );
};

const StatTile = ({ icon, label, value, color }) => {
  const tokens = useTokens();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.75,
        p: 2,
        borderRadius: 3,
        bgcolor: tokens.surface,
        border: `1px solid ${tokens.border}`,
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 2,
          display: "grid",
          placeItems: "center",
          bgcolor: `${color}1A`,
          color,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="h6" fontWeight={800} lineHeight={1.1} sx={{ color: tokens.ink }}>
          {value}
        </Typography>
        <Typography variant="caption" sx={{ color: tokens.inkFaint }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
};

const IntegrationCard = ({ item, connected, loading, onOpen }) => {
  const tokens = useTokens();
  const soon = item.status === "soon";
  const { Icon } = item;
  return (
    <Card
      variant="outlined"
      onClick={() => onOpen(item)}
      sx={{
        height: "100%",
        borderRadius: 3.5,
        borderColor: tokens.border,
        bgcolor: tokens.surface,
        backgroundImage: "none",
        cursor: "pointer",
        transition: "transform .15s ease, box-shadow .15s ease, border-color .15s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 10px 24px -12px rgba(20,23,31,0.25)",
          borderColor: item.color,
        },
      }}
    >
      <CardContent sx={{ p: 2.5, height: "100%", display: "flex", flexDirection: "column" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              bgcolor: item.color,
              display: "grid",
              placeItems: "center",
              boxShadow: `0 6px 16px -6px ${item.color}80`,
            }}
          >
            <Icon size={22} color="#fff" />
          </Box>
          {soon ? (
            <Chip
              size="small"
              icon={<FiClock size={12} />}
              label="Coming soon"
              sx={{ fontWeight: 700, bgcolor: tokens.soft, color: tokens.inkMuted }}
            />
          ) : (
            !loading && <StatusChip connected={connected} />
          )}
        </Stack>
        <Typography variant="subtitle1" fontWeight={700} sx={{ color: tokens.ink }}>
          {item.name}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: tokens.inkFaint, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}
        >
          {item.category}
        </Typography>
        <Typography variant="body2" sx={{ color: tokens.inkMuted, mt: 1, mb: 2, flex: 1 }}>
          {item.description}
        </Typography>
        <Button
          size="small"
          variant={!soon && !connected ? "contained" : "outlined"}
          endIcon={<FiArrowRight size={14} />}
          sx={{
            alignSelf: "flex-start",
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 2,
            boxShadow: "none",
            ...(!soon && !connected
              ? { bgcolor: item.color, color: "#fff", "&:hover": { bgcolor: item.color, filter: "brightness(0.92)" } }
              : { color: tokens.ink, borderColor: tokens.border }),
          }}
        >
          {soon ? "Learn more" : connected ? "Manage" : "Connect"}
        </Button>
      </CardContent>
    </Card>
  );
};

const ComingSoonDialog = ({ item, onClose }) => {
  const tokens = useTokens();
  if (!item) return null;
  const { Icon } = item;
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, bgcolor: tokens.surface, backgroundImage: "none" } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: item.color, display: "grid", placeItems: "center" }}>
            <Icon size={18} color="#fff" />
          </Box>
          <Box>
            <Typography fontWeight={800} sx={{ color: tokens.ink }}>
              {item.name}
            </Typography>
            <Chip size="small" label="Coming soon" sx={{ height: 20, fontWeight: 700, bgcolor: tokens.soft, color: tokens.inkMuted }} />
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
          {item.description} This integration isn't available yet. It's on our roadmap and will appear here as soon as it's ready.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} variant="contained" sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, boxShadow: "none", color: "#fff" }}>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DetailShell = ({ item, connected, actions, tabs, tab, onTab, onBack, children }) => {
  const tokens = useTokens();
  const { Icon } = item;
  return (
    <>
      <Button
        onClick={onBack}
        startIcon={<FiArrowLeft size={16} />}
        sx={{ mb: 1.5, textTransform: "none", fontWeight: 700, color: tokens.inkMuted, px: 1, "&:hover": { bgcolor: tokens.hoverTint, color: tokens.ink } }}
      >
        Back to Integrations
      </Button>
      <Breadcrumbs separator={<FiChevronRight size={12} />} sx={{ mb: 2, color: tokens.inkFaint }}>
        <Link component="button" underline="hover" onClick={onBack} sx={{ color: tokens.inkMuted, fontSize: 13, fontWeight: 600 }}>
          Integrations
        </Link>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: tokens.ink }}>{item.name}</Typography>
      </Breadcrumbs>

      <Card variant="outlined" sx={{ borderRadius: 4, borderColor: tokens.border, overflow: "hidden", bgcolor: tokens.surface, backgroundImage: "none" }}>
        <Box sx={{ height: 4, bgcolor: item.color }} />
        <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3.5 } }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={2} mb={2}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: { xs: 46, sm: 56 },
                  height: { xs: 46, sm: 56 },
                  flexShrink: 0,
                  borderRadius: 3,
                  bgcolor: item.color,
                  display: "grid",
                  placeItems: "center",
                  boxShadow: `0 6px 16px -6px ${item.color}80`,
                }}
              >
                <Icon size={26} color="#fff" />
              </Box>
              <Box>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="h6" fontWeight={800} sx={{ color: tokens.ink }}>
                    {item.name}
                  </Typography>
                  {connected && <StatusChip connected />}
                </Stack>
                <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
                  {item.description}
                </Typography>
              </Box>
            </Stack>
            <Box sx={{ "& > .MuiButton-root": { width: { xs: "100%", sm: "auto" } } }}>{actions}</Box>
          </Stack>

          <Tabs
            value={tab}
            onChange={(_, v) => onTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{ sx: { bgcolor: item.color } }}
            sx={{
              borderBottom: `1px solid ${tokens.border}`,
              mb: { xs: 2, sm: 3 },
              minHeight: 44,
              "& .MuiTab-root": { textTransform: "none", fontWeight: 700, color: tokens.inkMuted },
              "& .Mui-selected": { color: `${tokens.ink} !important` },
            }}
          >
            {tabs.map((t) => (
              <Tab key={t.id} value={t.id} label={t.label} />
            ))}
          </Tabs>
          {children}
        </CardContent>
      </Card>
    </>
  );
};

const Integration = () => {
  const tokens = useTokens();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: status, isLoading, refetch } = useGetAgentWhatsappStatusQuery();
  const { connect, isConnecting } = useAgentWhatsappConnect();

  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [soonItem, setSoonItem] = useState(null);
  const [connectOpen, setConnectOpen] = useState(false);

  const wa = searchParams.get("wa");
  const appId = searchParams.get("app");
  const tab = searchParams.get("tab");
  const isConnected = !!status?.connected;

  // Legacy redirect outcome of the embedded signup (`?wa=<result>`).
  useEffect(() => {
    if (!wa) return;
    const result = WA_CALLBACK_MESSAGES[wa];
    if (result) {
      if (result.type === "success") toast.success(result.text);
      else toast.error(result.text);
    }
    if (wa === "connected") refetch();
    const next = new URLSearchParams(searchParams);
    next.delete("wa");
    setSearchParams(next, { replace: true });
  }, [wa, refetch, searchParams, setSearchParams]);

  const connectedMap = { whatsapp: isConnected };
  const loadingMap = { whatsapp: isLoading };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return INTEGRATIONS.filter(
      (i) =>
        (category === "All" || i.category === category) &&
        (!q || i.name.toLowerCase().includes(q) || i.description.toLowerCase().includes(q))
    );
  }, [query, category]);

  const connectedCount = INTEGRATIONS.filter((i) => connectedMap[i.id]).length;
  const availableCount = INTEGRATIONS.filter((i) => i.status === "available").length;
  const soonCount = INTEGRATIONS.length - availableCount;

  const openItem = (item) => {
    if (item.status === "soon") return setSoonItem(item);
    setSearchParams({ app: item.id });
  };
  const goBack = () => setSearchParams({});
  const setTab = (id) => setSearchParams({ app: appId, tab: id });

  const active = INTEGRATIONS.find((i) => i.id === appId && i.status === "available");

  const handleSelectFlow = (existing) => {
    setConnectOpen(false);
    connect(existing);
  };

  let content;
  if (active?.id === "whatsapp") {
    const tabs = [{ id: "overview", label: "Overview" }];
    if (isConnected) tabs.push({ id: "inbox", label: "Inbox" });
    const cur = tabs.some((t) => t.id === tab) ? tab : "overview";
    content = (
      <DetailShell
        item={active}
        connected={isConnected}
        actions={
          !isLoading && !isConnected ? (
            <Button
              variant="contained"
              onClick={() => setConnectOpen(true)}
              disabled={isConnecting}
              sx={{ bgcolor: tokens.wa, color: "#fff", "&:hover": { bgcolor: tokens.waDark }, textTransform: "none", fontWeight: 700, borderRadius: 2, boxShadow: "none" }}
            >
              Connect WhatsApp
            </Button>
          ) : null
        }
        tabs={tabs}
        tab={cur}
        onTab={setTab}
        onBack={goBack}
      >
        {cur === "overview" && (
          <AgentWhatsappCard
            status={status}
            isLoading={isLoading}
            isConnecting={isConnecting}
            onConnect={() => setConnectOpen(true)}
            onOpenInbox={() => setTab("inbox")}
          />
        )}
        {cur === "inbox" && <AgentWhatsappInbox />}
      </DetailShell>
    );
  } else {
    content = (
      <>
        <Stack spacing={0.5} mb={3}>
          <Typography variant="overline" sx={{ color: tokens.fb, fontWeight: 700, letterSpacing: 1.2 }}>
            Integrations
          </Typography>
          <Typography variant="h5" fontWeight={800} sx={{ color: tokens.ink, letterSpacing: -0.3 }}>
            Integration Hub
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
            Connect your lead sources and messaging channels so everything flows into the CRM.
          </Typography>
        </Stack>

        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={4}>
            <StatTile icon={<FiCheckCircle size={20} />} label="Connected" value={connectedCount} color={tokens.success} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatTile icon={<FiGrid size={20} />} label="Available now" value={availableCount} color={tokens.fb} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatTile icon={<FiClock size={20} />} label="Coming soon" value={soonCount} color={tokens.warning} />
          </Grid>
        </Grid>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} mb={3}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.75, py: 0.5, borderRadius: 2.5, bgcolor: tokens.surface, border: `1px solid ${tokens.border}`, minWidth: { md: 300 } }}>
            <FiSearch color={tokens.inkFaint} />
            <InputBase
              placeholder="Search integrations"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ flex: 1, color: tokens.ink, fontSize: 14 }}
            />
          </Box>
          <Stack direction="row" spacing={1} sx={{ overflowX: "auto", pb: 0.5 }}>
            {CATEGORIES.map((c) => (
              <Chip
                key={c}
                label={c}
                onClick={() => setCategory(c)}
                sx={{
                  fontWeight: 700,
                  flexShrink: 0,
                  bgcolor: category === c ? tokens.ink : tokens.surface,
                  color: category === c ? tokens.surface : tokens.inkMuted,
                  border: `1px solid ${category === c ? tokens.ink : tokens.border}`,
                  "&:hover": { bgcolor: category === c ? tokens.ink : tokens.hoverTint },
                }}
              />
            ))}
          </Stack>
        </Stack>

        {filtered.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography fontWeight={700} sx={{ color: tokens.ink }}>
              No integrations found
            </Typography>
            <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
              Try a different search or category.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2.5}>
            {filtered.map((item) => (
              <Grid item xs={12} sm={6} lg={4} key={item.id}>
                <IntegrationCard item={item} connected={!!connectedMap[item.id]} loading={!!loadingMap[item.id]} onOpen={openItem} />
              </Grid>
            ))}
          </Grid>
        )}
      </>
    );
  }

  return (
    <Box sx={{ mx: "auto", bgcolor: tokens.page, p: { xs: 1.5, sm: 2, md: 4 }, borderRadius: 3, overflowX: "hidden" }}>
      {content}
      <ComingSoonDialog item={soonItem} onClose={() => setSoonItem(null)} />
      <ConnectWhatsAppDialog open={connectOpen} onClose={() => setConnectOpen(false)} onSelect={handleSelectFlow} />
    </Box>
  );
};

export default Integration;
